import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Card, Button, Form, ListGroup, Modal, Badge, Row, Col, ButtonGroup, Tabs, Tab, Table, Spinner, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Plus, Trash2, Share, PencilSquare, ZoomIn, ZoomOut, ArrowsFullscreen, Download, GraphUp, Calculator, Grid3x3 } from 'react-bootstrap-icons';
import axios from 'axios';
import { toast } from 'react-toastify';
import cytoscape from 'cytoscape';
import { Line } from 'react-chartjs-2';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

// Safe toast wrapper to prevent errors if ToastContainer isn't ready
const safeToast = {
  success: (msg) => { try { toast.success(msg); } catch (e) { console.log(msg); } },
  error: (msg) => { try { toast.error(msg); } catch (e) { console.error(msg); } },
  info: (msg) => { try { toast.info(msg); } catch (e) { console.log(msg); } },
};

// Layout configurations
const LAYOUTS = {
  cose: { name: 'cose', animate: true, animationDuration: 500, nodeOverlap: 20, componentSpacing: 100 },
  hierarchical: { name: 'breadthfirst', directed: true, spacingFactor: 1.5, animate: true },
  circular: { name: 'circle', animate: true, spacingFactor: 1.2 },
  concentric: { name: 'concentric', animate: true, minNodeSpacing: 50, concentric: (node) => node.data('isPrimary') ? 10 : 1, levelWidth: () => 2 },
  grid: { name: 'grid', animate: true, spacingFactor: 1.5 }
};

// Helper to get color based on price change
const getPerformanceColor = (changePercent) => {
  if (changePercent === undefined || changePercent === null) return '#666';
  return changePercent >= 0 ? '#28a745' : '#dc3545';
};

// Helper to calculate node size based on market cap (log scale)
const getNodeSize = (marketCap) => {
  if (!marketCap) return 40;
  const logCap = Math.log10(marketCap);
  return Math.min(Math.max(30 + (logCap - 9) * 8, 30), 80);
};

// Enhanced Cytoscape Graph component
const CytoscapeGraph = ({
  elements,
  stylesheet,
  layout,
  onNodeClick,
  cyRef: externalCyRef
}) => {
  const containerRef = useRef(null);
  const cyRef = useRef(null);
  const layoutRef = useRef(null);
  const mountedRef = useRef(true);
  const onNodeClickRef = useRef(onNodeClick);

  // Keep callback ref updated
  useEffect(() => {
    onNodeClickRef.current = onNodeClick;
  }, [onNodeClick]);

  useEffect(() => {
    mountedRef.current = true;
    if (!containerRef.current) return;

    cyRef.current = cytoscape({
      container: containerRef.current,
      style: stylesheet,
      minZoom: 0.3,
      maxZoom: 3,
    });

    // Event handlers using refs to avoid stale closures
    cyRef.current.on('tap', 'node', (evt) => {
      if (onNodeClickRef.current) onNodeClickRef.current(evt.target.data());
    });

    cyRef.current.on('mouseover', 'node', (evt) => {
      evt.target.style('border-width', 4);
      evt.target.style('border-color', '#fff');
    });

    cyRef.current.on('mouseout', 'node', (evt) => {
      evt.target.style('border-width', 2);
      evt.target.style('border-color', '#333');
    });

    if (externalCyRef) {
      externalCyRef.current = cyRef.current;
    }

    return () => {
      mountedRef.current = false;
      // Stop any running layout before destroying
      if (layoutRef.current) {
        layoutRef.current.stop();
        layoutRef.current = null;
      }
      if (cyRef.current) {
        cyRef.current.destroy();
        cyRef.current = null;
      }
      if (externalCyRef) {
        externalCyRef.current = null;
      }
    };
  }, [stylesheet, externalCyRef]);

  useEffect(() => {
    if (!cyRef.current || !elements || elements.length === 0 || !mountedRef.current) return;

    // Stop previous layout if running
    if (layoutRef.current) {
      layoutRef.current.stop();
    }

    cyRef.current.elements().remove();
    cyRef.current.add(elements);

    // Run layout without animation to prevent race conditions
    const layoutConfig = { ...layout, animate: false };
    layoutRef.current = cyRef.current.layout(layoutConfig);
    layoutRef.current.run();
  }, [elements, layout]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
};

// Format graph elements with performance-based styling
const formatGraphElements = (selectedStock, dependencies, primaryQuote) => {
  const elements = [];
  if (!selectedStock) return elements;

  const primaryChange = primaryQuote?.changesPercentage || 0;
  const primaryMarketCap = primaryQuote?.marketCap;

  // Add primary stock as a node
  elements.push({
    data: {
      id: selectedStock.ticker,
      label: selectedStock.ticker,
      type: 'stock',
      isPrimary: true,
      change: primaryChange,
      marketCap: primaryMarketCap,
      price: primaryQuote?.price,
      nodeColor: getPerformanceColor(primaryChange),
      nodeSize: getNodeSize(primaryMarketCap)
    }
  });

  dependencies.forEach(dep => {
    if (dep && dep.id && dep.dependent && dep.dependency_type) {
      const depChange = dep.performance?.changesPercentage;
      const depMarketCap = dep.performance?.marketCap;

      // Add dependent entity as a node
      if (!elements.some(el => el.data.id === dep.dependent)) {
        elements.push({
          data: {
            id: dep.dependent,
            label: dep.dependent,
            type: dep.dependency_type.toLowerCase(),
            isPrimary: false,
            change: depChange,
            marketCap: depMarketCap,
            price: dep.performance?.price,
            volume: dep.performance?.volume,
            correlation: dep.correlation_coefficient,
            estimates: dep.estimates,
            nodeColor: getPerformanceColor(depChange),
            nodeSize: getNodeSize(depMarketCap)
          }
        });
      }

      // Calculate edge thickness based on correlation
      const correlation = Math.abs(dep.correlation_coefficient || 0.5);
      const edgeWidth = 2 + correlation * 6;

      // Add edge
      elements.push({
        data: {
          id: `edge-${dep.id}`,
          source: selectedStock.ticker,
          target: dep.dependent,
          label: `${dep.relationship_type} (${dep.strength})`,
          strength: dep.strength,
          direction: dep.direction,
          relationship_type: dep.relationship_type,
          correlation: dep.correlation_coefficient,
          edgeWidth: edgeWidth
        }
      });
    }
  });
  return elements;
};

// Stylesheet for Cytoscape graph
const cyStylesheet = [
  {
    selector: 'node',
    style: {
      'background-color': 'data(nodeColor)',
      'label': 'data(label)',
      'text-valign': 'center',
      'color': '#fff',
      'width': 'data(nodeSize)',
      'height': 'data(nodeSize)',
      'font-size': '12px',
      'font-weight': 'bold',
      'text-outline-color': '#333',
      'text-outline-width': 2,
      'border-width': 2,
      'border-color': '#333'
    }
  },
  {
    selector: 'node[isPrimary]',
    style: {
      'shape': 'star',
      'border-width': 3,
      'border-color': '#ffd700'
    }
  },
  {
    selector: 'node[type="stock"]',
    style: {
      'shape': 'ellipse'
    }
  },
  {
    selector: 'node[type="country"]',
    style: {
      'shape': 'hexagon',
      'background-color': '#50e3c2'
    }
  },
  {
    selector: 'node[type="sector"]',
    style: {
      'shape': 'round-rectangle',
      'background-color': '#f5a623'
    }
  },
  {
    selector: 'node[type="industry"]',
    style: {
      'shape': 'diamond',
      'background-color': '#9b59b6'
    }
  },
  {
    selector: 'node[type="supply_chain"]',
    style: {
      'shape': 'round-octagon',
      'background-color': '#3498db'
    }
  },
  {
    selector: 'edge',
    style: {
      'width': 'data(edgeWidth)',
      'line-color': '#ccc',
      'target-arrow-color': '#ccc',
      'target-arrow-shape': 'triangle',
      'curve-style': 'bezier',
      'label': 'data(label)',
      'text-rotation': 'autorotate',
      'color': '#555',
      'font-size': '10px',
      'text-background-color': '#fff',
      'text-background-opacity': 0.8,
      'text-background-padding': '2px'
    }
  },
  {
    selector: 'edge[strength="Critical"]',
    style: {
      'line-color': '#e74c3c',
      'target-arrow-color': '#e74c3c'
    }
  },
  {
    selector: 'edge[strength="High"]',
    style: {
      'line-color': '#f5a623',
      'target-arrow-color': '#f5a623'
    }
  },
  {
    selector: 'edge[strength="Medium"]',
    style: {
      'line-color': '#4a90e2',
      'target-arrow-color': '#4a90e2'
    }
  },
  {
    selector: 'edge[strength="Low"]',
    style: {
      'line-color': '#28a745',
      'target-arrow-color': '#28a745'
    }
  },
  {
    selector: 'edge[direction="Upstream"]',
    style: {
      'target-arrow-shape': 'triangle',
      'arrow-scale': 1.2
    }
  },
  {
    selector: 'edge[direction="Downstream"]',
    style: {
      'source-arrow-shape': 'triangle',
      'target-arrow-shape': 'none',
      'arrow-scale': 1.2
    }
  },
  {
    selector: 'edge[direction="Lateral"]',
    style: {
      'target-arrow-shape': 'none',
      'source-arrow-shape': 'none',
      'line-style': 'dashed'
    }
  }
];

// Performance Panel Component
const PerformancePanel = ({ nodeData, primaryTicker, token }) => {
  const [historicalData, setHistoricalData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Only fetch for stock_to_stock type nodes (actual stock tickers)
    if (nodeData && nodeData.type === 'stock_to_stock' && primaryTicker && token) {
      fetchHistoricalData();
    } else {
      setHistoricalData(null);
    }
  }, [nodeData, primaryTicker, token]);

  const fetchHistoricalData = async () => {
    if (!nodeData || !primaryTicker || !token) return;
    setLoading(true);
    try {
      const requests = [
        axios.get(`${API_BASE}/api/stock-market/historical/${primaryTicker}`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ];

      if (nodeData.id !== primaryTicker) {
        requests.push(
          axios.get(`${API_BASE}/api/stock-market/historical/${nodeData.id}`, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => ({ data: null }))
        );
      }

      const results = await Promise.all(requests);
      const primaryRes = results[0];
      const selectedRes = results[1] || { data: null };

      setHistoricalData({
        primary: primaryRes.data?.historical?.slice(0, 30).reverse() || [],
        selected: selectedRes.data?.historical?.slice(0, 30).reverse() || []
      });
    } catch (error) {
      // Silently handle errors - historical data is optional
      setHistoricalData(null);
    }
    setLoading(false);
  };

  if (!nodeData) {
    return (
      <div className="text-center text-muted py-4">
        <GraphUp size={32} className="mb-2" />
        <p>Click on a node to see performance details</p>
      </div>
    );
  }

  const chartData = historicalData ? {
    labels: historicalData.primary.map(d => d.date?.slice(5) || ''),
    datasets: [
      {
        label: primaryTicker,
        data: historicalData.primary.map(d => d.close),
        borderColor: '#4a90e2',
        backgroundColor: 'rgba(74, 144, 226, 0.1)',
        tension: 0.4,
        fill: true
      },
      ...(historicalData.selected.length > 0 ? [{
        label: nodeData.id,
        data: historicalData.selected.map(d => d.close),
        borderColor: getPerformanceColor(nodeData.change),
        backgroundColor: 'rgba(0, 0, 0, 0.05)',
        tension: 0.4,
        fill: false
      }] : [])
    ]
  } : null;

  return (
    <div className="p-3">
      <h6 className="mb-3">{nodeData.label}</h6>

      <div className="mb-3">
        <Row>
          <Col xs={6}>
            <small className="text-muted">Price</small>
            <div className="fw-bold">${nodeData.price?.toFixed(2) || 'N/A'}</div>
          </Col>
          <Col xs={6}>
            <small className="text-muted">Change</small>
            <div className={`fw-bold ${nodeData.change >= 0 ? 'text-success' : 'text-danger'}`}>
              {nodeData.change?.toFixed(2) || 'N/A'}%
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={6}>
            <small className="text-muted">Market Cap</small>
            <div className="fw-bold">
              {nodeData.marketCap ? `$${(nodeData.marketCap / 1e9).toFixed(2)}B` : 'N/A'}
            </div>
          </Col>
          <Col xs={6}>
            <small className="text-muted">Volume</small>
            <div className="fw-bold">
              {nodeData.volume ? `${(nodeData.volume / 1e6).toFixed(2)}M` : 'N/A'}
            </div>
          </Col>
        </Row>
        {nodeData.correlation !== undefined && nodeData.correlation !== null && (
          <Row className="mt-2">
            <Col>
              <small className="text-muted">Correlation</small>
              <div className="fw-bold">
                <Badge bg={nodeData.correlation > 0.5 ? 'success' : nodeData.correlation > 0 ? 'warning' : 'danger'}>
                  {nodeData.correlation.toFixed(3)}
                </Badge>
              </div>
            </Col>
          </Row>
        )}
      </div>

      {loading ? (
        <div className="text-center py-3">
          <Spinner size="sm" />
        </div>
      ) : chartData && chartData.datasets[0].data.length > 0 ? (
        <div style={{ height: '200px' }}>
          <Line
            data={chartData}
            options={{
              responsive: true,
              maintainAspectRatio: false,
              plugins: { legend: { display: true, position: 'bottom' } },
              scales: {
                y: { beginAtZero: false }
              }
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

// Expected Changes Panel Component
const ExpectedChangesPanel = ({ dependencies, primaryTicker }) => {
  if (!dependencies || dependencies.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <p>No dependencies to analyze</p>
      </div>
    );
  }

  // Calculate expected changes based on correlation
  const stockDeps = dependencies.filter(d => d.dependency_type === 'STOCK_TO_STOCK');

  return (
    <div className="p-3">
      <h6 className="mb-3">Expected Impact Analysis</h6>
      <Table size="sm" striped>
        <thead>
          <tr>
            <th>Stock</th>
            <th>Correlation</th>
            <th>Strength</th>
            <th>Est. Impact</th>
          </tr>
        </thead>
        <tbody>
          {stockDeps.map(dep => {
            const corr = dep.correlation_coefficient || 0;
            const impact = Math.abs(corr * 100).toFixed(0);
            return (
              <tr key={dep.id}>
                <td>{dep.dependent}</td>
                <td>
                  <Badge bg={corr > 0.5 ? 'success' : corr > 0 ? 'warning' : 'danger'}>
                    {corr.toFixed(2)}
                  </Badge>
                </td>
                <td>
                  <Badge bg={
                    dep.strength === 'Critical' ? 'danger' :
                    dep.strength === 'High' ? 'warning' :
                    dep.strength === 'Medium' ? 'info' : 'success'
                  }>
                    {dep.strength}
                  </Badge>
                </td>
                <td>
                  <small>{impact}% of {primaryTicker} move</small>
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <h6 className="mt-4 mb-3">Analyst Estimates</h6>
      <Table size="sm" striped>
        <thead>
          <tr>
            <th>Stock</th>
            <th>Est. EPS</th>
            <th>Est. Revenue</th>
          </tr>
        </thead>
        <tbody>
          {stockDeps.filter(d => d.estimates).map(dep => (
            <tr key={dep.id}>
              <td>{dep.dependent}</td>
              <td>${dep.estimates?.estimatedEpsAvg?.toFixed(2) || 'N/A'}</td>
              <td>
                {dep.estimates?.estimatedRevenueAvg
                  ? `$${(dep.estimates.estimatedRevenueAvg / 1e9).toFixed(2)}B`
                  : 'N/A'}
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
};

// Correlation Heatmap Component
const CorrelationHeatmap = ({ dependencies, primaryTicker, onCellClick }) => {
  const stockDeps = dependencies.filter(d => d.dependency_type === 'STOCK_TO_STOCK');
  const tickers = [primaryTicker, ...stockDeps.map(d => d.dependent)];

  const getCorrelationColor = (corr) => {
    if (corr === null || corr === undefined) return '#f0f0f0';
    if (corr >= 0.7) return '#28a745';
    if (corr >= 0.3) return '#8bc34a';
    if (corr >= 0) return '#ffeb3b';
    if (corr >= -0.3) return '#ff9800';
    return '#dc3545';
  };

  // Build correlation matrix
  const getCorrelation = (ticker1, ticker2) => {
    if (ticker1 === ticker2) return 1;
    const dep = stockDeps.find(d =>
      (d.dependent === ticker1) || (d.dependent === ticker2)
    );
    return dep?.correlation_coefficient || null;
  };

  if (stockDeps.length === 0) {
    return (
      <div className="text-center text-muted py-4">
        <Grid3x3 size={32} className="mb-2" />
        <p>Add STOCK_TO_STOCK dependencies to see correlation heatmap</p>
      </div>
    );
  }

  return (
    <div className="p-3">
      <h6 className="mb-3">Correlation Matrix</h6>
      <div className="table-responsive">
        <Table size="sm" bordered className="text-center" style={{ fontSize: '11px' }}>
          <thead>
            <tr>
              <th></th>
              {tickers.map(t => <th key={t}>{t}</th>)}
            </tr>
          </thead>
          <tbody>
            {tickers.map(row => (
              <tr key={row}>
                <td className="fw-bold">{row}</td>
                {tickers.map(col => {
                  const corr = getCorrelation(row, col);
                  return (
                    <td
                      key={col}
                      style={{
                        backgroundColor: getCorrelationColor(corr),
                        cursor: corr !== null ? 'pointer' : 'default'
                      }}
                      onClick={() => corr !== null && row !== col && onCellClick && onCellClick(row, col)}
                    >
                      {corr !== null ? corr.toFixed(2) : '-'}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </Table>
      </div>
      <div className="d-flex justify-content-center gap-2 mt-2">
        <small><span style={{ backgroundColor: '#dc3545', padding: '2px 8px', color: '#fff' }}>-1</span></small>
        <small><span style={{ backgroundColor: '#ff9800', padding: '2px 8px' }}>-0.3</span></small>
        <small><span style={{ backgroundColor: '#ffeb3b', padding: '2px 8px' }}>0</span></small>
        <small><span style={{ backgroundColor: '#8bc34a', padding: '2px 8px' }}>0.3</span></small>
        <small><span style={{ backgroundColor: '#28a745', padding: '2px 8px', color: '#fff' }}>1</span></small>
      </div>
    </div>
  );
};


const DependencyNetwork = ({ profileId, watchlist, stocks, token }) => {
  const dependencyTypes = ['STOCK_TO_STOCK', 'SUPPLY_CHAIN', 'SECTOR', 'COUNTRY', 'INDUSTRY'];
  const relationshipTypes = ['Supplier', 'Customer', 'Competitor', 'Direct', 'Indirect'];
  const strengthLevels = ['Critical', 'High', 'Medium', 'Low'];
  const directions = ['Upstream', 'Downstream', 'Lateral'];
  const countries = ['China', 'Japan', 'South Korea', 'Taiwan', 'Singapore', 'India', 'Vietnam', 'Thailand', 'Malaysia', 'Philippines', 'Indonesia', 'USA', 'Canada', 'Mexico', 'Germany', 'UK', 'France', 'Italy', 'Spain', 'Netherlands', 'Belgium', 'Sweden', 'Switzerland', 'Australia', 'Brazil', 'Russia'];
  const sectors = ['Technology', 'Healthcare', 'Financials', 'Industrials', 'Consumer Discretionary', 'Consumer Staples', 'Energy', 'Utilities', 'Real Estate', 'Materials', 'Communication Services'];
  const industries = ['Semiconductors', 'Software', 'Pharmaceuticals', 'Biotechnology', 'Hospitals', 'Banks', 'Insurance', 'Machinery', 'Automobiles', 'Retailers', 'Oil & Gas', 'Electric Utilities', 'Real Estate', 'Metals & Mining', 'Chemicals', 'Paper & Forest Products', 'Telecom Services', 'Internet Services', 'Aerospace & Defense'];
  const supplyChainEntities = ['Supplier', 'Manufacturer', 'Distributor', 'Logistics Provider', 'Raw Materials Provider', 'Contract Manufacturer', 'Reseller', 'Retailer', 'Wholesaler'];

  const [dependencies, setDependencies] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    primary_ticker: '',
    dependent_ticker: '',
    dependency_type: 'STOCK_TO_STOCK',
    relationship_type: 'Supplier',
    strength: 'Medium',
    direction: 'Upstream'
  });
  const [selectedStock, setSelectedStock] = useState(stocks[0] || null);
  const [selectedLayout, setSelectedLayout] = useState('cose');
  const [selectedNode, setSelectedNode] = useState(null);
  const [primaryQuote, setPrimaryQuote] = useState(null);
  const [calculatingCorrelation, setCalculatingCorrelation] = useState(null);
  const [activeTab, setActiveTab] = useState('performance');
  const cyRef = useRef(null);

  // Memoize graph elements to prevent unnecessary re-renders
  const graphElements = useMemo(() => {
    return formatGraphElements(selectedStock, dependencies, primaryQuote);
  }, [selectedStock, dependencies, primaryQuote]);

  // Memoize layout config
  const currentLayout = useMemo(() => {
    return LAYOUTS[selectedLayout];
  }, [selectedLayout]);

  useEffect(() => {
    if (selectedStock && selectedStock.ticker) {
      fetchDependencies(selectedStock.ticker);
      fetchPrimaryQuote(selectedStock.ticker);
    } else {
      setDependencies([]);
      setPrimaryQuote(null);
    }
  }, [selectedStock]);

  const fetchPrimaryQuote = async (ticker) => {
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/fmp/${ticker}/quote`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPrimaryQuote(response.data);
    } catch (error) {
      console.error('Error fetching primary quote:', error);
    }
  };

  const getDependentOptions = () => {
    switch (formData.dependency_type) {
      case 'STOCK_TO_STOCK':
        return stocks.filter(s => s.id !== selectedStock?.id).map(s => ({ value: s.ticker, label: `${s.ticker} - ${s.company_name}` }));
      case 'COUNTRY':
        return countries.map(c => ({ value: c, label: c }));
      case 'SECTOR':
        return sectors.map(s => ({ value: s, label: s }));
      case 'INDUSTRY':
        return industries.map(i => ({ value: i, label: i }));
      case 'SUPPLY_CHAIN':
        return supplyChainEntities.map(e => ({ value: e, label: e }));
      default:
        return [];
    }
  };

  const fetchDependencies = async (ticker) => {
    if (!ticker) return;
    try {
      const response = await axios.get(
        `${API_BASE}/api/stock-market/stocks/${ticker}/dependencies`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { profile_id: profileId }
        }
      );
      const depsData = response.data.dependencies || response.data;
      setDependencies(Array.isArray(depsData) ? depsData : []);
    } catch (error) {
      // Dependency endpoints are optional in Java backend parity; keep UI quiet on fetch failures.
      setDependencies([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedStock) {
      safeToast.error('Select a stock first');
      return;
    }

    const payload = {
      dependent_ticker: formData.dependent_ticker,
      strength: formData.strength,
      relationship_type: formData.relationship_type,
      direction: formData.direction,
      notes: formData.notes || '',
      ...(!editingId && { profile_id: profileId }),
      ...(!editingId && { primary_stock_id: selectedStock.id }),
      ...(!editingId && { dependency_type: formData.dependency_type })
    };

    try {
      if (editingId) {
        await axios.put(
          `${API_BASE}/api/stock-market/dependencies/${editingId}`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        safeToast.success('Dependency updated');
        setEditingId(null);
      } else {
        await axios.post(
          `${API_BASE}/api/stock-market/dependencies`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        safeToast.success('Dependency created');
      }

      setShowModal(false);
      fetchDependencies(selectedStock.ticker);
      setFormData({
        primary_ticker: selectedStock.ticker,
        dependent_ticker: '',
        dependency_type: 'STOCK_TO_STOCK',
        relationship_type: 'Supplier',
        strength: 'Medium',
        direction: 'Upstream',
        notes: ''
      });
    } catch (error) {
      safeToast.error(error.response?.data?.detail || `Failed to ${editingId ? 'update' : 'create'} dependency`);
    }
  };

  const handleEdit = (dep) => {
    setEditingId(dep.id);
    setFormData({
      dependent_ticker: dep.dependent,
      relationship_type: dep.relationship_type || 'Supplier',
      direction: dep.direction || 'Upstream',
      strength: dep.strength,
      dependency_type: dep.dependency_type,
      notes: dep.notes || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (depId) => {
    if (window.confirm('Delete this dependency?')) {
      try {
        await axios.delete(
          `${API_BASE}/api/stock-market/dependencies/${depId}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        safeToast.success('Dependency deleted');
        if (selectedStock) fetchDependencies(selectedStock.ticker);
      } catch (error) {
        safeToast.error('Failed to delete dependency');
      }
    }
  };

  const handleCalculateCorrelation = async (depId) => {
    setCalculatingCorrelation(depId);
    try {
      const response = await axios.post(
        `${API_BASE}/api/stock-market/dependencies/${depId}/calculate-correlation`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      safeToast.success(`Correlation: ${response.data.correlation_coefficient.toFixed(3)}`);
      fetchDependencies(selectedStock.ticker);
    } catch (error) {
      safeToast.error(error.response?.data?.detail || 'Failed to calculate correlation');
    }
    setCalculatingCorrelation(null);
  };

  const handleNodeClick = useCallback((nodeData) => {
    setSelectedNode(nodeData);
  }, []);

  const handleZoomIn = () => {
    if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() * 1.2);
  };

  const handleZoomOut = () => {
    if (cyRef.current) cyRef.current.zoom(cyRef.current.zoom() * 0.8);
  };

  const handleFitToScreen = () => {
    if (cyRef.current) cyRef.current.fit();
  };

  const handleExportImage = () => {
    if (cyRef.current) {
      const png = cyRef.current.png({ scale: 2, bg: '#fff' });
      const link = document.createElement('a');
      link.href = png;
      link.download = `dependency-graph-${selectedStock?.ticker || 'export'}.png`;
      link.click();
    }
  };

  const getStrengthColor = (strength) => {
    const colors = {
      'Critical': 'danger',
      'High': 'warning',
      'Medium': 'info',
      'Low': 'success'
    };
    return colors[strength] || 'secondary';
  };

  return (
    <>
      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Dependency Network</h5>
          <Button variant="primary" size="sm" onClick={() => setShowModal(true)} disabled={!selectedStock}>
            <Plus size={16} /> Add Dependency
          </Button>
        </Card.Header>

        <Card.Body>
          <Form.Group className="mb-3">
            <Form.Label>Primary Stock</Form.Label>
            <Form.Select
              value={selectedStock?.id || ''}
              onChange={(e) => {
                const stock = stocks.find(s => s.id === parseInt(e.target.value));
                if (stock) setSelectedStock(stock);
              }}
            >
              <option>Choose a stock...</option>
              {stocks.map(stock => (
                <option key={stock.id} value={stock.id}>
                  {stock.ticker} - {stock.company_name}
                </option>
              ))}
            </Form.Select>
          </Form.Group>

          {selectedStock && dependencies.length > 0 && (
            <ListGroup>
              {dependencies.map(dep => (
                <ListGroup.Item key={dep.id} className="mb-2">
                  <Row className="align-items-center">
                    <Col md={3}>
                      <div className="fw-bold">{selectedStock.ticker}</div>
                      <Badge bg={getStrengthColor(dep.strength)}>{dep.strength}</Badge>
                    </Col>
                    <Col md={2} className="text-center">
                      <small className="text-muted">{dep.direction}</small>
                      {dep.correlation_coefficient !== null && dep.correlation_coefficient !== undefined && (
                        <div>
                          <Badge bg="secondary" className="mt-1">
                            r={dep.correlation_coefficient.toFixed(2)}
                          </Badge>
                        </div>
                      )}
                    </Col>
                    <Col md={3}>
                      <div className="fw-bold">{dep.dependent || 'Unknown'}</div>
                      <Badge bg="secondary">{dep.dependency_type}</Badge>
                    </Col>
                    <Col md={4} className="text-end gap-1 d-flex justify-content-end">
                      {dep.dependency_type === 'STOCK_TO_STOCK' && (
                        <OverlayTrigger
                          placement="top"
                          overlay={<Tooltip>Calculate Correlation</Tooltip>}
                        >
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => handleCalculateCorrelation(dep.id)}
                            disabled={calculatingCorrelation === dep.id}
                          >
                            {calculatingCorrelation === dep.id ? (
                              <Spinner size="sm" />
                            ) : (
                              <Calculator size={14} />
                            )}
                          </Button>
                        </OverlayTrigger>
                      )}
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handleEdit(dep)}
                      >
                        <PencilSquare size={14} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleDelete(dep.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </Col>
                  </Row>
                  {dep.notes && <small className="text-muted d-block mt-2">{dep.notes}</small>}
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}

          {selectedStock && dependencies.length === 0 && (
            <div className="text-muted text-center py-4">
              No dependencies found for {selectedStock.ticker}
            </div>
          )}
        </Card.Body>
      </Card>

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">Dependency Graph</h6>
          <div className="d-flex gap-2 align-items-center">
            <Form.Select
              size="sm"
              value={selectedLayout}
              onChange={(e) => setSelectedLayout(e.target.value)}
              style={{ width: '150px' }}
            >
              <option value="cose">Force-Directed</option>
              <option value="hierarchical">Hierarchical</option>
              <option value="circular">Circular</option>
              <option value="concentric">Concentric</option>
              <option value="grid">Grid</option>
            </Form.Select>
            <ButtonGroup size="sm">
              <Button variant="outline-secondary" onClick={handleZoomIn}>
                <ZoomIn size={14} />
              </Button>
              <Button variant="outline-secondary" onClick={handleZoomOut}>
                <ZoomOut size={14} />
              </Button>
              <Button variant="outline-secondary" onClick={handleFitToScreen}>
                <ArrowsFullscreen size={14} />
              </Button>
              <Button variant="outline-secondary" onClick={handleExportImage}>
                <Download size={14} />
              </Button>
            </ButtonGroup>
          </div>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={8}>
              {selectedStock && dependencies.length > 0 ? (
                <div style={{ height: '500px', border: '1px solid #dee2e6', borderRadius: '0.5rem' }}>
                  <CytoscapeGraph
                    elements={graphElements}
                    stylesheet={cyStylesheet}
                    layout={currentLayout}
                    onNodeClick={handleNodeClick}
                    cyRef={cyRef}
                  />
                </div>
              ) : (
                <div className="text-center py-5 text-muted" style={{ height: '500px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', border: '1px dashed #dee2e6', borderRadius: '0.5rem' }}>
                  <Share size={48} className="mb-3" />
                  <p>No dependencies yet. Add dependencies to see the graph.</p>
                </div>
              )}
            </Col>
            <Col md={4}>
              <Card className="h-100">
                <Card.Body className="p-0" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k)}
                    className="mb-0"
                    fill
                  >
                    <Tab eventKey="performance" title="Performance">
                      <PerformancePanel
                        nodeData={selectedNode}
                        primaryTicker={selectedStock?.ticker}
                        token={token}
                      />
                    </Tab>
                    <Tab eventKey="expected" title="Expected">
                      <ExpectedChangesPanel
                        dependencies={dependencies}
                        primaryTicker={selectedStock?.ticker}
                      />
                    </Tab>
                    <Tab eventKey="heatmap" title="Heatmap">
                      <CorrelationHeatmap
                        dependencies={dependencies}
                        primaryTicker={selectedStock?.ticker}
                        onCellClick={(t1, t2) => {
                          // Find and highlight the relationship
                          if (cyRef.current) {
                            const edgeId = dependencies.find(d =>
                              d.dependent === t1 || d.dependent === t2
                            )?.id;
                            if (edgeId) {
                              cyRef.current.$(`#edge-${edgeId}`).select();
                            }
                          }
                        }}
                      />
                    </Tab>
                  </Tabs>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => { setShowModal(false); setEditingId(null); }}>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Edit Dependency' : 'Add Dependency'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleSubmit}>
            <Form.Group className="mb-3">
              <Form.Label>Primary Stock</Form.Label>
              <Form.Control type="text" value={selectedStock?.ticker || ''} disabled />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Dependency Type *</Form.Label>
              <Form.Select
                value={formData.dependency_type}
                onChange={(e) => setFormData({ ...formData, dependency_type: e.target.value, dependent_ticker: '' })}
                disabled={!!editingId}
              >
                {dependencyTypes.map(type => (
                  <option key={type} value={type}>{type.replace(/_/g, ' ')}</option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted small">
                {formData.dependency_type === 'STOCK_TO_STOCK' && 'Select another stock from your watchlist'}
                {formData.dependency_type === 'SUPPLY_CHAIN' && 'Select supply chain entity type'}
                {formData.dependency_type === 'SECTOR' && 'Select a sector'}
                {formData.dependency_type === 'COUNTRY' && 'Select a country'}
                {formData.dependency_type === 'INDUSTRY' && 'Select an industry'}
              </Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                {formData.dependency_type === 'COUNTRY' && 'Country'}
                {formData.dependency_type === 'SECTOR' && 'Sector'}
                {formData.dependency_type === 'INDUSTRY' && 'Industry'}
                {formData.dependency_type === 'SUPPLY_CHAIN' && 'Supply Chain Entity'}
                {formData.dependency_type === 'STOCK_TO_STOCK' && 'Dependent Stock'}
                {' '}*
              </Form.Label>
              <Form.Select
                value={formData.dependent_ticker}
                onChange={(e) => setFormData({ ...formData, dependent_ticker: e.target.value })}
                required
              >
                <option value="">-- Select an option --</option>
                {getDependentOptions().map((option, idx) => (
                  <option key={idx} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Relationship Type</Form.Label>
              <Form.Select
                value={formData.relationship_type}
                onChange={(e) => setFormData({ ...formData, relationship_type: e.target.value })}
              >
                {relationshipTypes.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Strength *</Form.Label>
              <Form.Select
                value={formData.strength}
                onChange={(e) => setFormData({ ...formData, strength: e.target.value })}
              >
                {strengthLevels.map(level => (
                  <option key={level} value={level}>{level}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Direction *</Form.Label>
              <Form.Select
                value={formData.direction}
                onChange={(e) => setFormData({ ...formData, direction: e.target.value })}
              >
                {directions.map(dir => (
                  <option key={dir} value={dir}>{dir}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Notes</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this dependency"
              />
            </Form.Group>

            <Button variant="primary" type="submit" className="w-100">
              {editingId ? 'Update Dependency' : 'Create Dependency'}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default DependencyNetwork;
